/* eslint-disable react-hooks/exhaustive-deps */
import {
    Box,
    Button,
    Card,
    CardContent,
    Collapse,
    Divider,
    Typography,
    Tooltip,
} from '@mui/material';
import {Add, Close, Sync, ExpandLess, ExpandMore, RateReview} from '@mui/icons-material';
import React, {useCallback, useEffect, useState} from 'react';
import {deleteBranch, getBranches, postBranch, syncBranches} from '../http/clinicAPI';
import useSnackbar from '../hooks/useSnackbar';
import MySnackbar from '../components/MySnackbar';
import BranchForm from '../components/BranchForm';
import ReviewLinksModal from '../components/ReviewLinksModal';
import LinkIcon from '@mui/icons-material/Link';

const BranchesSettings = () => {
    const [branches, setBranches] = useState([]);
    const [isAddingBranch, setIsAddingBranch] = useState(false);
    const [expandedBranchId, setExpandedBranchId] = useState(null);
    const [loadingBranchId, setLoadingBranchId] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [reviewLinksModalOpen, setReviewLinksModalOpen] = useState(false);
    const [currentBranchForLinks, setCurrentBranchForLinks] = useState(null);
    const snack = useSnackbar(false, 'Ошибка');

    const fetchBranches = useCallback(async () => {
        try {
            const data = await getBranches();
            setBranches(data);
        } catch (error) {
            snack.handleChange(true, 'Ошибка при загрузке филиалов', 'error');
        }
    }, []);

    useEffect(() => {
        fetchBranches();
    }, [fetchBranches]);

    const handleSyncBranches = async () => {
        setIsSyncing(true);
        try {
            const result = await syncBranches();
            const message = result.addedCount > 0 
                ? `Синхронизация завершена. Добавлено филиалов: ${result.addedCount}`
                : 'Синхронизация завершена. Новых филиалов не обнаружено';
            snack.handleChange(true, message, 'success');
            await fetchBranches();
        } catch (error) {
            snack.handleChange(true, error.message || 'Ошибка синхронизации филиалов', 'error');
        } finally {
            setIsSyncing(false);
        }
    };

    const handleRemoveBranch = async (id) => {
        try {
            await deleteBranch({id});
            snack.handleChange(true, 'Филиал удалён', 'success');
            setBranches((prev) => prev.filter((branch) => branch.id !== id));
        } catch (error) {
            snack.handleChange(true, error.message, 'error');
        }
    };

    const handleSaveBranch = async ({formData, existingImages, imagePreviews, setExistingImages, setImagePreviews, setFormData}, branchId = null) => {
        const isEdit = !!branchId;
        setLoadingBranchId(branchId || 'new');
        
        try {
            const formDataToSend = new FormData();
            
            if (isEdit) {
                formDataToSend.append('id', branchId);
                formDataToSend.append('action', 'edit');
                formDataToSend.append('isActive', String(formData.isActive));
                
                // Для редактирования обрабатываем удаленные изображения
                const branch = branches.find(b => b.id === branchId);
                const originalImages = Array.isArray(branch?.mapImageName) ? branch.mapImageName : [];
                const deletedImages = originalImages.filter(img => !existingImages.includes(img));
                
                if (deletedImages.length > 0) {
                    formDataToSend.append('mapImagesToDelete', JSON.stringify(deletedImages));
                }
                
                if (existingImages.length === 0 && formData.mapImages.length === 0 && originalImages.length > 0) {
                    formDataToSend.append('removeMapImage', 'true');
                }
            } else {
                formDataToSend.append('action', 'create');
                formDataToSend.append('isActive', 'true');
            }
            
            // Общие поля
            formDataToSend.append('name', formData.name);
            formDataToSend.append('misName', formData.misName);
            formDataToSend.append('reviewLinksConfig', JSON.stringify(formData.reviewLinksConfig || {links: {}, order: []}));
            formDataToSend.append('address', formData.address);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('workSchedule', JSON.stringify(formData.workSchedule));
            formDataToSend.append('timezoneOffset', formData.timezoneOffset);
            formDataToSend.append('hasOwnSystem', formData.hasOwnSystem ? 'true' : 'false');
            
            if (formData.hasOwnSystem) {
                formDataToSend.append('systemSecrets', JSON.stringify({
                    apiKey: formData.apiKey,
                    webApiAddress: formData.webApiAddress
                }));
            }
            
            if (formData.mapImages.length > 0) {
                formData.mapImages.forEach(file => {
                    formDataToSend.append('mapImage', file);
                });
            }
            
            formDataToSend.append('showHowToGetButton', formData.showHowToGetButton ? 'true' : 'false');
            formDataToSend.append('howToGetDescription', formData.howToGetDescription || '');

            const data = await postBranch(formDataToSend);
            
            if (data.error) throw new Error(data.error);
            
            snack.handleChange(true, 'Сохранено', 'success');
            
            if (isEdit) {
                // Обновляем существующие изображения
                if (setExistingImages) {
                    setExistingImages(Array.isArray(data.mapImageName) ? data.mapImageName : []);
                }
                if (setFormData) {
                    setFormData((prev) => ({...prev, mapImages: []}));
                }
                await fetchBranches();
            } else {
                // Добавляем новый филиал
                setBranches((prev) => [data, ...prev]);
                setIsAddingBranch(false);
            }
            
            // Очищаем превью
            if (imagePreviews) {
                imagePreviews.forEach(preview => URL.revokeObjectURL(preview.url));
                if (setImagePreviews) setImagePreviews([]);
            }
        } catch (error) {
            snack.handleChange(true, error.message, 'error');
        } finally {
            setLoadingBranchId(null);
        }
    };

    const toggleBranchExpand = (branchId) => {
        setExpandedBranchId(prev => prev === branchId ? null : branchId);
    };

    const handleOpenReviewLinksModal = (branch) => {
        setCurrentBranchForLinks(branch);
        setReviewLinksModalOpen(true);
    };

    const handleSaveReviewLinks = async (config) => {
        if (!currentBranchForLinks) return;
        
        setLoadingBranchId(currentBranchForLinks.id);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('id', currentBranchForLinks.id);
            formDataToSend.append('action', 'edit');
            formDataToSend.append('name', currentBranchForLinks.name);
            formDataToSend.append('misName', currentBranchForLinks.misName);
            formDataToSend.append('address', currentBranchForLinks.address);
            formDataToSend.append('phone', currentBranchForLinks.phone);
            formDataToSend.append('isActive', String(currentBranchForLinks.isActive));
            formDataToSend.append('reviewLinksConfig', JSON.stringify(config));
            formDataToSend.append('workSchedule', JSON.stringify(currentBranchForLinks.workSchedule || {}));
            formDataToSend.append('timezoneOffset', currentBranchForLinks.timezoneOffset || -180);
            formDataToSend.append('hasOwnSystem', currentBranchForLinks.hasOwnSystem ? 'true' : 'false');
            
            if (currentBranchForLinks.hasOwnSystem) {
                formDataToSend.append('systemSecrets', JSON.stringify(currentBranchForLinks.systemSecrets || {}));
            }
            
            formDataToSend.append('showHowToGetButton', currentBranchForLinks.showHowToGetButton ? 'true' : 'false');
            formDataToSend.append('howToGetDescription', currentBranchForLinks.howToGetDescription || '');

            const data = await postBranch(formDataToSend);
            
            if (data.error) throw new Error(data.error);
            
            snack.handleChange(true, 'Ссылки на отзывы сохранены', 'success');
            await fetchBranches();
        } catch (error) {
            snack.handleChange(true, error.message, 'error');
        } finally {
            setLoadingBranchId(null);
            setCurrentBranchForLinks(null);
        }
    };

    return (
        <Box
            sx={{
                p: 2,
                maxWidth: 1200,
                margin: '0 auto',
                width: '100%',
            }}
        >
            <MySnackbar {...snack} />

            <Card variant="outlined" sx={{mb: 3}}>
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">Управление филиалами</Typography>
                    <Box sx={{display: 'flex', gap: 1}}>
                        <Button
                            variant="outlined"
                            startIcon={<Sync/>}
                            onClick={handleSyncBranches}
                            disabled={isSyncing}
                            sx={{
                                borderRadius: 2,
                                flexShrink: 0,
                            }}
                        >
                            {isSyncing ? 'Подождите...' : 'Подгрузить из МИС'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={isAddingBranch ? <Close/> : <Add/>}
                            onClick={() => setIsAddingBranch(!isAddingBranch)}
                            sx={{
                                borderRadius: 2,
                                flexShrink: 0,
                            }}
                        >
                            {isAddingBranch ? 'Отмена' : 'Добавить'}
                        </Button>
                    </Box>
                </Box>

                <Collapse in={isAddingBranch}>
                    <Divider/>
                    <CardContent sx={{pt: 2}}>
                        <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                            }}
                        >
                            Новый филиал
                        </Typography>

                        <BranchForm
                            onSubmit={(data) => handleSaveBranch(data)}
                            isLoading={loadingBranchId === 'new'}
                            submitButtonText="Сохранить"
                        />
                    </CardContent>
                </Collapse>
            </Card>

            <Typography
                variant="h6"
                sx={{
                    mb: 2,
                    mt: 4,
                    px: 1,
                }}
            >
                Список филиалов ({branches.length})
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                }}
            >
                {branches.map((branch) => (
                    <Card
                        key={branch.id}
                        sx={{
                            minWidth: 275,
                            border: 1,
                            borderColor: 'grey.300',
                            borderRadius: 1,
                            boxShadow: 1,
                        }}
                        elevation={0}
                    >
                        <Box
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box 
                                sx={{flex: 1, cursor: 'pointer'}}
                                onClick={() => toggleBranchExpand(branch.id)}
                            >
                                <Typography variant="subtitle1" sx={{fontWeight: expandedBranchId === branch.id ? 600 : 400}}>
                                    {branch.name || 'Без названия'}
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Tooltip title="Настроить ссылки на отзывы">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<RateReview />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenReviewLinksModal(branch);
                                        }}
                                        sx={{borderRadius: 1.5}}
                                    >
                                        Отзывы
                                    </Button>
                                </Tooltip>
                                {Boolean(branch.misId) && (
                                    <Tooltip title="Филиал связан с МИС">
                                        <LinkIcon color="success"/>
                                    </Tooltip>
                                )}
                                <Box sx={{cursor: 'pointer'}} onClick={() => toggleBranchExpand(branch.id)}>
                                    {expandedBranchId === branch.id ? <ExpandLess/> : <ExpandMore/>}
                                </Box>
                            </Box>
                        </Box>
                        
                        <Collapse in={expandedBranchId === branch.id}>
                            <Divider/>
                            <CardContent>
                                <BranchForm
                                    initialData={branch}
                                    onSubmit={(data) => handleSaveBranch(data, branch.id)}
                                    onDelete={handleRemoveBranch}
                                    isLoading={loadingBranchId === branch.id}
                                    submitButtonText="Сохранить"
                                />
                            </CardContent>
                        </Collapse>
                    </Card>
                ))}
            </Box>

            <ReviewLinksModal
                open={reviewLinksModalOpen}
                onClose={() => {
                    setReviewLinksModalOpen(false);
                    setCurrentBranchForLinks(null);
                }}
                initialConfig={currentBranchForLinks?.reviewLinksConfig}
                onSave={handleSaveReviewLinks}
            />
        </Box>
    );
};

export default BranchesSettings;
